import { Box, Grid2, Skeleton } from "@mui/material";
import { neutral } from "@flat-ui";
import {
  FaroTextField,
  FaroTextFieldProps,
} from "@components/common/faro-text-field/faro-text-field";
import { formatNumber } from "@utils/data-display";
import { useMemo } from "react";
import { useMediaQueryList } from "@hooks/use-media-query";

/** Common style for the info bar items */
const infoBarItemStyle = {
  bgcolor: neutral[50],
  padding: 2.5,
  height: {
    xs: "80px",
    sm: "90px",
    md: "100px",
  },
};

/** Font sizes for different screen sizes using rem units */
const fontSizes = {
  sm: "1.125rem",
  md: "1.5rem",
  lg: "2rem",
};

type Item = Pick<FaroTextFieldProps, "label" | "initialValue">;

interface Props {
  /** Flag whether something is being load or not */
  isLoading?: boolean;

  /** The items to show in the info bar */
  items?: Item[];
}

/**
 * Component that shows an information bar about the current dialog.
 */
export function DialogInfoBar({
  isLoading = false,
  items,
}: Props): JSX.Element {
  const { isScreenSmOrSmaller, isScreenMdOrSmaller, isScreenMdOrLarger } =
    useMediaQueryList();

  const valueFontSize = useMemo(() => {
    if (isScreenSmOrSmaller) {
      return fontSizes.sm;
    }

    if (isScreenMdOrSmaller) {
      return fontSizes.md;
    }

    if (isScreenMdOrLarger) {
      return fontSizes.lg;
    }
  }, [isScreenMdOrLarger, isScreenMdOrSmaller, isScreenSmOrSmaller]);

  if (isLoading) {
    return (
      <Box component="div" height={infoBarItemStyle.height}>
        <Skeleton variant="rectangular" height="100%" />
      </Box>
    );
  }

  return (
    <Grid2 container spacing={1}>
      {items?.map((item, index) => (
        <Grid2 size={{ xs: 12, sm: 6 }} key={index}>
          <Box sx={infoBarItemStyle}>
            <FaroTextField
              label={item.label}
              initialValue={formatNumber(item.initialValue)}
              faroVariant="main"
              fontSize={valueFontSize}
            />
          </Box>
        </Grid2>
      ))}
    </Grid2>
  );
}
